export type WorkflowProgressJsonValue =
  | null
  | boolean
  | number
  | string
  | WorkflowProgressJsonValue[]
  | { [key: string]: WorkflowProgressJsonValue };

export type WorkflowProgressValue = {
  key: string;
  label: string;
  value: WorkflowProgressJsonValue;
  unit?: string;
  type?: string;
};

export type WorkflowProgressMetadata = Record<string, WorkflowProgressJsonValue>;

type JsonRecord = { [key: string]: WorkflowProgressJsonValue };

type ValueDescriptor = {
  label?: string;
  unit?: string;
  type?: string;
};

export type WorkflowProgressEnvelope = {
  version?: number;
  scope: "run" | "step";
  stepId?: string;
  scopeKey?: string;
  payload: WorkflowProgressJsonValue;
  metadata?: WorkflowProgressMetadata;
  generation?: string;
  attempt?: number;
  revision?: number;
  idempotencyKey?: string;
  updatedAt?: string;
};

export type WorkflowProgressQuery = (
  | { scope: "run"; scopeKey?: string }
  | { scope: "step"; stepId: string; scopeKey?: string }
);

export function workflowProgressPath(
  runId: string,
  source: string,
  query: WorkflowProgressQuery = { scope: "run" },
): string {
  const params = new URLSearchParams({
    source,
    scope: query.scope,
  });
  if (query.scope === "step") params.set("stepId", query.stepId);
  if (query.scopeKey) params.set("scopeKey", query.scopeKey);
  return `/runs/${encodeURIComponent(runId)}/progress?${params.toString()}`;
}

export type WorkflowProgressDisplay = {
  payload: WorkflowProgressJsonValue;
  metadata?: WorkflowProgressMetadata;
};

function isJsonRecord(value: WorkflowProgressJsonValue): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isWorkflowProgressJsonValue(value: unknown): value is WorkflowProgressJsonValue {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isWorkflowProgressJsonValue);
  if (typeof value !== "object") return false;
  return Object.values(value as Record<string, unknown>).every(isWorkflowProgressJsonValue);
}

function isWorkflowProgressScope(value: unknown): value is "run" | "step" {
  return value === "run" || value === "step";
}

function isWorkflowProgressMetadata(value: unknown): value is WorkflowProgressMetadata {
  return isWorkflowProgressJsonValue(value) && isJsonRecord(value);
}

function readWorkflowProgressEnvelope(value: unknown): WorkflowProgressEnvelope | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!isWorkflowProgressScope(record.scope) || !isWorkflowProgressJsonValue(record.payload)) return null;
  if (record.scope === "step" && typeof record.stepId !== "string") return null;
  if (record.metadata !== undefined && !isWorkflowProgressMetadata(record.metadata)) return null;
  return {
    ...(typeof record.version === "number" ? { version: record.version } : {}),
    scope: record.scope,
    ...(typeof record.stepId === "string" ? { stepId: record.stepId } : {}),
    ...(typeof record.scopeKey === "string" ? { scopeKey: record.scopeKey } : {}),
    payload: record.payload,
    ...(record.metadata !== undefined ? { metadata: record.metadata } : {}),
    ...(typeof record.generation === "string" ? { generation: record.generation } : {}),
    ...(typeof record.attempt === "number" ? { attempt: record.attempt } : {}),
    ...(typeof record.revision === "number" ? { revision: record.revision } : {}),
    ...(typeof record.idempotencyKey === "string" ? { idempotencyKey: record.idempotencyKey } : {}),
    ...(typeof record.updatedAt === "string" ? { updatedAt: record.updatedAt } : {}),
  };
}

/**
 * The workflow API embeds the generic envelope beside legacy step fields.
 * Unwrap that envelope for display, while retaining legacy fields as opaque
 * values so older producers remain visible without workflow-specific logic.
 */
export function workflowProgressDisplay(progress: unknown): WorkflowProgressDisplay | null {
  if (progress === null || progress === undefined) return null;
  if (!isWorkflowProgressJsonValue(progress)) return null;
  const envelope = readWorkflowProgressEnvelope(progress);
  if (envelope) return { payload: envelope.payload, metadata: envelope.metadata };
  if (isJsonRecord(progress)) {
    const nestedEnvelope = readWorkflowProgressEnvelope(progress.workflowProgress);
    if (nestedEnvelope) {
      const legacy = Object.fromEntries(
        Object.entries(progress).filter(([key]) => key !== "workflowProgress"),
      ) as JsonRecord;
      const payload = isJsonRecord(nestedEnvelope.payload)
        ? { ...legacy, ...nestedEnvelope.payload }
        : { ...legacy, value: nestedEnvelope.payload };
      return { payload, metadata: nestedEnvelope.metadata };
    }
  }
  return { payload: progress };
}

function descriptorFor(metadata: WorkflowProgressMetadata | undefined, key: string): ValueDescriptor {
  const candidate = metadata?.[key];
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return {};
  const value = candidate as Record<string, unknown>;
  return {
    label: typeof value.label === "string" ? value.label : undefined,
    unit: typeof value.unit === "string" ? value.unit : undefined,
    type: typeof value.type === "string" ? value.type : undefined,
  };
}

export function workflowProgressValueEntries(
  payload: WorkflowProgressJsonValue,
  metadata?: WorkflowProgressMetadata,
): WorkflowProgressValue[] {
  const values = isJsonRecord(payload) ? payload : { value: payload };
  return Object.entries(values).map(([key, value]) => {
    const descriptor = descriptorFor(metadata, key);
    return {
      key,
      label: descriptor.label ?? key,
      value,
      ...(descriptor.unit ? { unit: descriptor.unit } : {}),
      ...(descriptor.type ? { type: descriptor.type } : {}),
    };
  });
}

export function formatWorkflowProgressValue(value: WorkflowProgressJsonValue): string {
  if (typeof value === "string") return value;
  if (value === null) return "null";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

export function workflowProgressLines(
  progress: unknown,
  metadata?: WorkflowProgressMetadata,
): string[] {
  const display = workflowProgressDisplay(progress);
  if (!display) return [];
  return workflowProgressValueEntries(display.payload, metadata ?? display.metadata).map(({ label, value, unit }) => {
    const unitLabel = unit ? ` (${unit})` : "";
    return `${label}: ${formatWorkflowProgressValue(value)}${unitLabel}`;
  });
}

/** Compact row summary selected by the producer; full progress stays in detail. */
export function workflowProgressRowLines(progress: unknown): string[] {
  const display = workflowProgressDisplay(progress);
  if (!display) return [];
  const summary = display.metadata?.summary;
  if (!summary || typeof summary !== "object" || Array.isArray(summary)) return workflowProgressLines(progress);
  const descriptor = summary as Record<string, WorkflowProgressJsonValue>;
  const completedKey = descriptor.completedKey;
  const totalKey = descriptor.totalKey;
  if (typeof completedKey !== "string" || typeof totalKey !== "string") return workflowProgressLines(progress);
  const payload = isJsonRecord(display.payload) ? display.payload : {};
  const completed = payload[completedKey];
  const total = payload[totalKey];
  if (typeof completed !== "number" && typeof completed !== "string") {
    return [`${typeof descriptor.unit === "string" ? `${descriptor.unit[0]?.toUpperCase() ?? "I"}${descriptor.unit.slice(1)}` : "Items"}: ? / ${typeof total === "number" || typeof total === "string" ? String(total) : "?"}`];
  }
  const totalLabel = typeof total === "number" || typeof total === "string" ? String(total) : "?";
  const unit = typeof descriptor.unit === "string" ? descriptor.unit : "items";
  return [`${unit[0]?.toUpperCase() ?? "I"}${unit.slice(1)}: ${String(completed)} / ${totalLabel}`];
}
